import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import * as iconRegistry from './AppIcon';
import { AppIcon } from './AppIcon';
import { appColor, cn, normalize } from '../lib/utils';

/**
 * Sélecteur visuel d'icône.
 *
 * L'éditeur du catalogue réclamait jusqu'ici le nom exact d'une icône lucide,
 * saisi de mémoire. Personne ne connaît cette cinquantaine de noms : on montre
 * donc les glyphes, et la recherche ne sert qu'à réduire la grille quand on
 * sait déjà ce qu'on cherche.
 */

interface IconGroup {
    label: string;
    names: string[];
}

/** Intitulé de groupe déduit du nom de l'export qui porte la liste. */
function groupLabel(exportName: string): string {
    if (/BRAND|LOGO/i.test(exportName)) return 'Logos de marque';
    if (exportName === 'AVAILABLE_ICONS') return 'Icônes générales';
    return 'Autres icônes';
}

/**
 * Rassemble les listes de noms exposées par le registre d'icônes.
 *
 * Le registre expose aujourd'hui `AVAILABLE_ICONS`, et d'autres listes peuvent
 * s'y ajouter, les logos de marque par exemple. Plutôt que de les nommer une à
 * une, on ramasse tout export qui est un tableau de chaînes : une liste
 * supplémentaire paraît alors dans le sélecteur sans qu'il faille y revenir.
 *
 * `AVAILABLE_ICONS` ouvre la marche, le reste suit par ordre d'export. Un nom
 * présent dans deux listes n'est retenu que par la première.
 */
function buildGroups(): IconGroup[] {
    const registry = iconRegistry as unknown as Record<string, unknown>;

    const lists = Object.keys(registry)
        .filter((key) => Array.isArray(registry[key]))
        .sort((a, b) => {
            if (a === 'AVAILABLE_ICONS') return -1;
            if (b === 'AVAILABLE_ICONS') return 1;
            return a.localeCompare(b);
        });

    const seen = new Set<string>();
    const groups = new Map<string, string[]>();

    for (const key of lists) {
        const names = (registry[key] as unknown[]).filter(
            (value): value is string => typeof value === 'string' && !seen.has(value)
        );
        if (names.length === 0) continue;

        names.forEach((name) => seen.add(name));

        const label = groupLabel(key);
        groups.set(label, [...(groups.get(label) ?? []), ...names]);
    }

    return [...groups].map(([label, names]) => ({ label, names }));
}

const ICON_GROUPS = buildGroups();
const NOMS_CONNUS = new Set(ICON_GROUPS.flatMap((group) => group.names));

interface IconPickerProps {
    value: string;
    onChange: (name: string) => void;
    /** Couleur d'accent de l'application, pour que l'aperçu montre le rendu réel. */
    color: string;
}

export function IconPicker({ value, onChange, color }: IconPickerProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const groups = useMemo(() => {
        const needle = normalize(search.trim());
        if (needle === '') return ICON_GROUPS;

        return ICON_GROUPS.map((group) => ({
            label: group.label,
            names: group.names.filter((name) => normalize(name).includes(needle)),
        })).filter((group) => group.names.length > 0);
    }, [search]);

    const matchCount = groups.reduce((total, group) => total + group.names.length, 0);

    useEffect(() => {
        if (!open) return;

        searchRef.current?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // La modale de l'éditeur écoute elle aussi la touche : sans cet
                // arrêt, fermer la grille refermerait le formulaire entier.
                event.stopPropagation();
                setOpen(false);
            }
        };

        const onPointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };

        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('mousedown', onPointerDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open]);

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className="input flex items-center gap-2.5 text-left"
                aria-expanded={open}
                aria-controls="icon-picker-panel"
            >
                <span
                    className="app-swatch flex items-center justify-center w-7 h-7 rounded-lg shrink-0"
                    style={appColor(color)}
                    aria-hidden
                >
                    <AppIcon name={value} className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0 truncate font-mono text-xs">{value}</span>
                <ChevronDown
                    className={cn(
                        'w-4 h-4 shrink-0 text-[var(--color-text-muted)] transition-transform',
                        open && 'rotate-180'
                    )}
                    aria-hidden
                />
            </button>

            {/* Le catalogue est ancien et son champ « icon » était libre : un nom
                hérité peut ne plus rien désigner. AppIcon retombe alors sur une
                icône générique, ce que rien ne signalait jusqu'ici. */}
            {!NOMS_CONNUS.has(value) && (
                <p className="mt-1 text-xs text-[var(--color-warning)]">
                    Ce nom ne figure pas au registre : le catalogue affiche l’icône par défaut.
                </p>
            )}

            {open && (
                <div
                    id="icon-picker-panel"
                    className="card mt-1.5 p-2.5 space-y-2.5 shadow-lg"
                >
                    <div className="relative">
                        <Search
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none"
                            aria-hidden
                        />
                        <input
                            ref={searchRef}
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Filtrer par nom, ex. chart, cloud, shield"
                            className="input pl-9 pr-8 py-1.5 text-xs"
                            aria-label="Filtrer les icônes"
                        />
                        {search !== '' && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="btn btn-ghost absolute right-1 top-1/2 -translate-y-1/2 p-1"
                                aria-label="Effacer le filtre"
                            >
                                <X className="w-3.5 h-3.5" aria-hidden />
                            </button>
                        )}
                    </div>

                    {matchCount === 0 ? (
                        <p className="py-6 text-center text-xs text-[var(--color-text-secondary)]">
                            Aucune icône ne porte ce nom. Essayez un mot anglais, les noms du
                            registre en viennent.
                        </p>
                    ) : (
                        <div className="max-h-64 overflow-y-auto space-y-3 pr-0.5">
                            {groups.map((group) => (
                                <div key={group.label}>
                                    {/* L'intitulé ne sert que s'il y a plusieurs familles à
                                        distinguer, les icônes générales des logos de marque. */}
                                    {ICON_GROUPS.length > 1 && (
                                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                                            {group.label}
                                        </p>
                                    )}

                                    <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
                                        {group.names.map((name) => {
                                            const selected = name === value;

                                            return (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    onClick={() => {
                                                        onChange(name);
                                                        setOpen(false);
                                                    }}
                                                    title={name}
                                                    aria-label={name}
                                                    aria-pressed={selected}
                                                    className={cn(
                                                        'relative grid place-items-center h-10 rounded-lg border transition-colors',
                                                        selected
                                                            ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                                                            : 'border-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text)]'
                                                    )}
                                                >
                                                    <AppIcon name={name} className="w-5 h-5" />
                                                    {selected && (
                                                        <Check
                                                            className="absolute top-0.5 right-0.5 w-3 h-3"
                                                            aria-hidden
                                                        />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
